/**
 * DeviceRegistrationDialog — Diálogo premium para cadastrar o dispositivo atual.
 * Exibido APÓS passkey registrado/verificado com sucesso.
 *
 * Perguntas: (1) É somente seu? (2) Onde está? (3) Nome do dispositivo.
 * Resultado: POST /auth/security/approval/device/register
 */
import { useState, useEffect } from 'react';
import { Monitor, Smartphone, Home, MapPin, School, Lock, ShieldCheck, Users, Pencil } from 'lucide-react';
import { registerApprovalDevice } from '../../security/deviceApprovalApi';

interface Props {
  fingerprint: string;
  navegador?: string;
  sistemaOp?: string;
  onComplete: (somenteMeu: boolean) => void;
}

export function DeviceRegistrationDialog({ fingerprint, navegador, sistemaOp, onComplete }: Props) {
  const [step, setStep] = useState(1);
  const [somenteMeu, setSomenteMeu] = useState<boolean | null>(null);
  const [tipoLocal, setTipoLocal] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [saving, setSaving] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const deviceLabel = isMobile ? 'celular' : 'computador';
  const DeviceIcon = isMobile ? Smartphone : Monitor;

  useEffect(() => {
    if (isMobile) {
      setSomenteMeu(true);
      setTipoLocal('CASA');
      setStep(3);
      setNome(sistemaOp ? `${sistemaOp} — Celular` : 'Meu Celular');
    }
  }, [isMobile, sistemaOp]);

  const token = localStorage.getItem('ac_token') || '';

  async function salvar() {
    setSaving(true);
    try {
      await registerApprovalDevice(
        { fingerprint, nome: nome.trim() || undefined, tipoLocal, somenteMeu, navegador, sistemaOp },
        { bearerToken: token },
      );
      onComplete(!!somenteMeu);
    } catch { 
      onComplete(!!somenteMeu); 
    }
  }

  const stepIndicator = (current: number) => (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 20 }}>
      {[1, 2, 3].map(i => (
        <div key={i} style={{
          width: i === current ? 24 : 8, height: 8, borderRadius: 4,
          background: i < current ? '#10b981' : i === current ? '#6c5ce7' : '#e2e8f0',
          transition: 'all 0.3s ease',
        }} />
      ))}
    </div>
  );

  // Step 1: Público ou Privado?
  if (step === 1) {
    return (
      <div style={overlay}>
        <div style={card}>
          {stepIndicator(1)}

          <div style={headerIcon}>
            <DeviceIcon size={32} strokeWidth={1.8} style={{ color: '#6c5ce7' }} />
          </div>

          <h2 style={titleStyle}>Este {deviceLabel} é seu?</h2>
          <p style={subtitleStyle}>Isso define como protegemos sua conta aqui.</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', marginTop: 8 }}>
            <button
              onClick={() => { setSomenteMeu(true); setStep(2); }}
              onMouseEnter={() => setHovered('priv')}
              onMouseLeave={() => setHovered(null)}
              style={{
                ...optBtn,
                borderColor: hovered === 'priv' ? '#10b981' : 'rgba(0,0,0,0.06)',
                background: hovered === 'priv' ? '#f0fdf4' : '#ffffff',
              }}
            >
              <div style={{ ...optIcon, background: '#ecfdf5', color: '#10b981' }}>
                <Lock size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={optTitle}>Sim, só eu uso</div>
                <div style={optSub}>Acesso rápido por biometria ativado.</div>
              </div>
              <div style={{ color: '#10b981', fontSize: '1.2rem' }}>→</div>
            </button>

            <button
              onClick={() => { setSomenteMeu(false); setStep(2); }}
              onMouseEnter={() => setHovered('pub')}
              onMouseLeave={() => setHovered(null)}
              style={{
                ...optBtn,
                borderColor: hovered === 'pub' ? '#f59e0b' : 'rgba(0,0,0,0.06)',
                background: hovered === 'pub' ? '#fffbeb' : '#ffffff',
              }}
            >
              <div style={{ ...optIcon, background: '#fef3c7', color: '#d97706' }}>
                <Users size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={optTitle}>Não, é compartilhado</div>
                <div style={optSub}>Sem biometria. Login sempre pelo e-mail.</div>
              </div>
              <div style={{ color: '#f59e0b', fontSize: '1.2rem' }}>→</div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Onde está?
  if (step === 2) {
    const locais = [
      { id: 'ESCOLA', label: 'Na escola', sub: 'Secretaria, diretoria, laboratório', icon: School, color: '#3b82f6', bg: '#eff6ff' },
      { id: 'CASA', label: 'Em casa', sub: 'Uso pessoal, home office', icon: Home, color: '#10b981', bg: '#ecfdf5' },
      { id: 'OUTRO', label: 'Outro lugar', sub: 'LAN house, biblioteca...', icon: MapPin, color: '#8b5cf6', bg: '#f5f3ff' },
    ];

    return (
      <div style={overlay}>
        <div style={card}>
          {stepIndicator(2)}

          <div style={headerIcon}>
            <MapPin size={32} strokeWidth={1.8} style={{ color: '#6c5ce7' }} />
          </div>

          <h2 style={titleStyle}>Onde está este {deviceLabel}?</h2>
          <p style={subtitleStyle}>Ajuda a ajustar a segurança do acesso.</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', marginTop: 8 }}>
            {locais.map(l => (
              <button
                key={l.id}
                onClick={() => { setTipoLocal(l.id); setStep(3); }}
                onMouseEnter={() => setHovered(l.id)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  ...optBtn,
                  borderColor: hovered === l.id ? l.color : 'rgba(0,0,0,0.06)',
                  background: hovered === l.id ? l.bg : '#ffffff',
                }}
              >
                <div style={{ ...optIcon, background: l.bg, color: l.color }}>
                  <l.icon size={20} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={optTitle}>{l.label}</div>
                  <div style={optSub}>{l.sub}</div>
                </div>
                <div style={{ color: l.color, fontSize: '1.2rem' }}>→</div>
              </button>
            ))}
          </div>

          <button onClick={() => setStep(1)} style={backBtnStyle}>← Voltar</button>
        </div>
      </div>
    );
  }

  // Step 3: Nome
  return (
    <div style={overlay}>
      <div style={card}>
        {stepIndicator(3)}

        <div style={{ ...headerIcon, background: '#ecfdf5', borderColor: '#bbf7d0' }}>
          <ShieldCheck size={32} strokeWidth={1.8} style={{ color: '#16a34a' }} />
        </div>

        <h2 style={titleStyle}>Dê um nome a este {deviceLabel}</h2>
        <p style={subtitleStyle}>Para você reconhecê-lo na lista de dispositivos.</p>

        <div style={{ position: 'relative', width: '100%', marginTop: 4 }}>
          <Pencil size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text" placeholder={isMobile ? 'Ex: Meu Celular' : 'Ex: PC da Secretaria'}
            value={nome} onChange={e => setNome(e.target.value)}
            style={inputSt} autoFocus maxLength={60}
          />
        </div>

        {/* Resumo visual */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <span style={{ ...badge, background: somenteMeu ? '#ecfdf5' : '#fef3c7', color: somenteMeu ? '#16a34a' : '#d97706' }}>
            {somenteMeu ? '🔒 Uso exclusivo' : '👥 Compartilhado'}
          </span>
          <span style={{ ...badge, background: '#f1f5f9', color: '#475569' }}>
            {tipoLocal === 'ESCOLA' ? '🏫 Escola' : tipoLocal === 'CASA' ? '🏠 Casa' : '📍 Outro'}
          </span>
          {navegador && (
            <span style={{ ...badge, background: '#f1f5f9', color: '#475569' }}>
              🌐 {navegador}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, width: '100%', marginTop: 20 }}>
          {!isMobile && <button onClick={() => setStep(2)} style={{ ...backBtnStyle, flex: 1, marginTop: 0 }}>← Voltar</button>}
          <button onClick={salvar} disabled={saving} style={{
            ...saveBtnStyle,
            flex: isMobile ? undefined : 2,
            width: isMobile ? '100%' : 'auto',
            opacity: saving ? 0.7 : 1,
          }}>
            {saving ? 'Salvando...' : '✓ Registrar Dispositivo'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────
const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(12px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 99998, padding: 20,
  animation: 'dreg-fadeIn 0.3s ease',
};

const card: React.CSSProperties = {
  background: '#ffffff', borderRadius: 24, padding: 'clamp(24px, 5vw, 36px)',
  maxWidth: 420, width: '100%',
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
  boxShadow: '0 25px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.04)',
  animation: 'dreg-slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
};

const headerIcon: React.CSSProperties = {
  width: 64, height: 64, borderRadius: 20,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: '#f5f3ff', border: '2px solid #ddd6fe', marginBottom: 4,
};

const titleStyle: React.CSSProperties = {
  fontSize: '1.15rem', fontWeight: 800, color: '#1e293b', margin: '4px 0 0',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: '0.82rem', color: '#64748b', marginBottom: 8, textAlign: 'center',
};

const optBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 14,
  border: '2px solid rgba(0,0,0,0.06)', background: '#ffffff',
  cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s ease',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

const optIcon: React.CSSProperties = {
  width: 44, height: 44, borderRadius: 12, flexShrink: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const optTitle: React.CSSProperties = {
  fontSize: '0.9rem', fontWeight: 700, color: '#1e293b', marginBottom: 2,
};

const optSub: React.CSSProperties = {
  fontSize: '0.76rem', color: '#94a3b8', lineHeight: 1.3,
};

const backBtnStyle: React.CSSProperties = {
  padding: '10px 20px', borderRadius: 12, border: '1.5px solid #e2e8f0',
  background: '#f8fafc', color: '#64748b', cursor: 'pointer',
  fontSize: '0.82rem', fontWeight: 600, marginTop: 12, transition: 'all 0.2s',
};

const saveBtnStyle: React.CSSProperties = {
  flex: 2, padding: '13px 20px', borderRadius: 14, border: 'none',
  background: 'linear-gradient(135deg, #6c5ce7, #a29bfe)', color: '#fff',
  cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700,
  boxShadow: '0 6px 24px rgba(108, 92, 231, 0.3)',
  transition: 'all 0.2s',
};

const inputSt: React.CSSProperties = {
  width: '100%', padding: '13px 16px 13px 40px', borderRadius: 14,
  border: '2px solid #e2e8f0', background: '#f8fafc',
  color: '#1e293b', fontSize: '0.9rem', outline: 'none',
  transition: 'border-color 0.2s',
};

const badge: React.CSSProperties = {
  fontSize: '0.72rem', fontWeight: 600, padding: '4px 10px', borderRadius: 20,
};
